import React from 'react';

// Named-competitor comparison for the Buyer's Guide (LP-006). Desktop uses one
// grid so the label column and all 4 platform columns share row-tracks.
// Mobile stacks into one card per platform (label + value pairs) instead of a
// horizontal-scroll table.
const ROWS = [
  { label: 'Primary strength' },
  { label: 'Presentation / selections' },
  { label: 'Procurement / financials' },
  { label: 'Project management' },
  { label: 'Decision + approval history' },
  { label: 'Requires team upkeep' },
  { label: 'Builds record from existing communication' },
];

const RowDots = ({ filled }) => (
  <div className="flex items-center gap-1.5">
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < filled ? 'bg-[#3A4A32]' : 'bg-[#E4DAC9]'}`} />
    ))}
  </div>
);

const cellBg = (col) => (col.highlight ? 'bg-[#EDF1E6]' : 'bg-[#FDF8F3]');

export const PlatformComparisonTable = ({ columns, rowData }) => (
  <>
    {/* Mobile/tablet: one stacked card per platform */}
    <div className="md:hidden space-y-6">
      {columns.map((col, ci) => (
        <div
          key={col.name}
          data-testid={`platform-col-${ci}`}
          className={`rounded-2xl border overflow-hidden ${col.highlight ? 'border-2 border-[#5C6B4E]' : 'border-[#E7DDCB]'} ${cellBg(col)}`}
        >
          <div className="text-center pt-6 pb-5 border-b border-[#E7DDCB]">
            <p
              className={`text-[15px] font-semibold uppercase tracking-[0.03em] ${col.highlight ? 'text-[#3A4A32]' : 'text-[#1F1812]'}`}
              style={col.highlight ? { fontFamily: "'Cormorant Garamond', serif", fontSize: '17px' } : undefined}
            >
              {col.name}
            </p>
          </div>
          <div className="divide-y divide-[#E7DDCB]">
            {rowData.map((row, ri) => {
              const Icon = row.icon;
              return (
                <div key={row.label} className="px-6 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={15} strokeWidth={1.6} className="text-[#7A5A2E] shrink-0" />
                    <p className="text-[#3A342C] text-[12px] font-semibold tracking-[0.01em] uppercase">{row.label}</p>
                  </div>
                  {typeof col.values[ri] === 'number' ? (
                    <RowDots filled={col.values[ri]} />
                  ) : (
                    <p className="text-[#3D3A36] text-[14px]">{col.values[ri]}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>

    {/* Desktop: single grid so divider lines run unbroken across all columns */}
    <div className="hidden md:block overflow-x-auto">
      <div className="grid min-w-[880px]" style={{ gridTemplateColumns: '300px repeat(4, minmax(150px, 1fr))' }}>
        <div className="pb-5 border-b border-[#E7DDCB]" />
        {columns.map((col, ci) => (
          <div
            key={col.name}
            data-testid={`platform-col-desktop-${ci}`}
            className={`pb-5 pt-2 text-center border-b ${col.highlight ? 'border-[#5C6B4E] rounded-t-xl border-t-2 border-l-2 border-r-2' : 'border-[#E7DDCB]'} ${cellBg(col)}`}
          >
            <p
              className={`text-[15px] font-semibold uppercase tracking-[0.03em] ${col.highlight ? 'text-[#3A4A32]' : 'text-[#1F1812]'}`}
              style={col.highlight ? { fontFamily: "'Cormorant Garamond', serif", fontSize: '17px' } : undefined}
            >
              {col.name}
            </p>
          </div>
        ))}

        {rowData.map((row, ri) => {
          const Icon = row.icon;
          const last = ri === rowData.length - 1;
          return (
            <React.Fragment key={row.label}>
              <div className={`py-5 pr-4 flex items-center gap-3 ${last ? '' : 'border-b'} border-[#E7DDCB]`}>
                <Icon size={17} strokeWidth={1.6} className="text-[#7A5A2E] shrink-0" />
                <p className="text-[#3A342C] text-[13.5px] font-semibold tracking-[0.01em] uppercase">{row.label}</p>
              </div>
              {columns.map((col) => (
                <div
                  key={`${col.name}-${row.label}`}
                  className={`py-5 px-3 flex items-center justify-center ${last ? '' : 'border-b'} ${
                    col.highlight ? `border-[#5C6B4E] border-l-2 border-r-2 ${last ? 'rounded-b-xl border-b-2' : ''}` : 'border-[#E7DDCB]'
                  } ${cellBg(col)}`}
                >
                  {typeof col.values[ri] === 'number' ? (
                    <RowDots filled={col.values[ri]} />
                  ) : (
                    <p className="text-[#3D3A36] text-[14.5px] text-center">{col.values[ri]}</p>
                  )}
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </>
);

export default PlatformComparisonTable;
